import { ReactElement, useCallback, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Flex, LoadingIndicator, Text, Button } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { useAppContext } from "~/contexts/appContext";
import {
  useGetClientDataQuery,
  useGetFundFamilyCasesLazyQuery,
} from "~/graphql/supergraphHooks";
import { capitalizeString } from "~/pages/Billing/utils";
import { formatShortFormDate } from "~/utils/datetime";
import {
  hasBillingFARole,
  hasBillingGPRole,
  isBillingAdmin,
  isBillingMaker,
} from "~/utils/userRoles";
import { CaseDetailsDrawer } from "../CasesOverview/CaseDetailsDrawer";
import { BillingCaseType } from "../CasesOverview/types";
import { useBillingDate } from "../contexts/BillingDateContext";
import { EntitiesCard } from "./EntitiesCard";
import { EntityUpdateProvider } from "./EntityUpdateContext";
import { FilterSortProvider } from "./FilterSortContext";
import { LogTab } from "./LogTab";
import { OverviewCard } from "./OverviewCard";
import { PageHeaderCard, type ClientViewTab } from "./PageHeaderCard";
import { QuickLinksCard } from "./QuickLinksCard";
import {
  pageContainer,
  contentContainer,
  leftColumn,
  fullHeight,
  errorText,
} from "./styles_vanilla.css";
import { BILLING_SUMMARY_ITEMS } from "./types";
import type { BillingCaseRowItem } from "../CasesOverview/types";

export const ClientBillingOverview = (): ReactElement => {
  const { clientId } = useParams<{ clientId: string }>();
  const { asOfDateString } = useBillingDate();
  const { user } = useAppContext();
  const [drawerCase, setDrawerCase] = useState<BillingCaseRowItem | undefined>(undefined);
  const [pendingChangeRequestIds, setPendingChangeRequestIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<ClientViewTab>("overview");

  const userRoles = user?.roles ?? [];
  const canChangeClientInfo =
    isBillingMaker(userRoles) || isBillingAdmin(userRoles) || hasBillingGPRole(userRoles);
  const hasLogTabAccess = hasBillingFARole(userRoles) || hasBillingGPRole(userRoles);
  const isBillingEnableFADataFlagEnabled = useFeatureFlag("billingEnableFAData", false);
  const {
    loading,
    error,
    data: clientBillingData,
  } = useGetClientDataQuery({
    variables: {
      clientId: clientId || "",
      asOfDate: asOfDateString,
    },

    skip: !clientId,
    fetchPolicy: "no-cache",
  });

  const [fetchPendingCases] = useGetFundFamilyCasesLazyQuery({
    fetchPolicy: "network-only",
  });

  const handleChangeClientInfo = useCallback(async () => {
    if (
      !clientBillingData?.getClientData ||
      typeof clientBillingData.getClientData.clientArena.arenaId !== "number"
    ) {
      return;
    }
    const { clientArena } = clientBillingData.getClientData;

    const result = await fetchPendingCases({
      variables: {
        arenaId: clientArena.arenaId,
        clientId: Number(clientId),
        decision: "pending",
        offset: 0,
        limit: 20,
      },
    });

    const pendingCases = result.data?.getFundFamilyCases?.cases ?? [];

    // Always open a fresh draft so makers can submit any CR type (FCC or fund-family)
    // regardless of what is already pending for the client. Pending case IDs are passed
    // as context so the entities grid can display existing proposed changes alongside
    // the new draft. The BE enforces all conflict rules:
    //   - same type already open per client → type-level block
    //   - same entity in open cross-type CR → entity-level block (ISE-663)
    //   - cross-type for different entity → allowed
    setPendingChangeRequestIds(pendingCases.map((c) => c.id));
    setDrawerCase({
      changeRequestId: "",
      familyId: "",
      clientId: Number(clientId),
      billingCaseId: 0,
      type: BillingCaseType.ClientInformation,
      requestType: null,
      clientName: clientArena.clientName,
      creationDate: formatShortFormDate(new Date().toISOString()),
      status: "PENDING_REVIEW",
      resolution: null,
      assignee: user.fullName || null,
      secondaryAssignee: null,
      assigneeId: null,
      resolvedByName: null,
    });
  }, [clientBillingData, clientId, fetchPendingCases, user.fullName]);

  const handleDrawerClose = () => {
    setDrawerCase(undefined);
    setPendingChangeRequestIds([]);
  };

  if (loading) {
    return <LoadingIndicator variant="primary" />;
  }

  if (error) {
    return (
      <Flex alignItems="center" justifyContent="center" className={fullHeight}>
        <Text className={errorText}>Error: {error.message}</Text>
      </Flex>
    );
  }

  if (!clientBillingData?.getClientData) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        className={fullHeight}
        flexDirection="column"
        gap="16px"
      >
        <Text className={errorText}>
          No billing data available for the selected client and as of date.
        </Text>
        <Link to="/billing">
          <Button variant="primary">Go to Client List</Button>
        </Link>
      </Flex>
    );
  }

  const { clientArena, quickLinks } = clientBillingData.getClientData;

  return (
    <FilterSortProvider>
      <EntityUpdateProvider>
        <Flex className={pageContainer}>
          <PageHeaderCard
            clientName={clientArena.clientName}
            edition={capitalizeString(clientArena.edition)}
            summaryItems={BILLING_SUMMARY_ITEMS(clientArena, isBillingEnableFADataFlagEnabled)}
            clientId={clientId}
            clientArenaShowSpinner={clientArena.showSpinner}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {activeTab === "overview" && (
            <Flex className={contentContainer}>
              <Flex className={leftColumn}>
                <OverviewCard clientData={clientArena} />
                <QuickLinksCard quickLinksData={quickLinks} arenaId={clientArena.arenaId} />
              </Flex>
              <EntitiesCard
                clientId={clientId || ""}
                clientName={clientArena.clientName}
                onChangeClientInfo={canChangeClientInfo ? handleChangeClientInfo : undefined}
              />
            </Flex>
          )}

          {activeTab === "log" && hasLogTabAccess && !Number.isNaN(Number(clientId)) && (
            <LogTab clientId={Number(clientId)} arenaId={clientArena.arenaId} />
          )}
        </Flex>
        <CaseDetailsDrawer
          billingCase={drawerCase}
          onClose={handleDrawerClose}
          pendingChangeRequestIds={pendingChangeRequestIds}
        />
      </EntityUpdateProvider>
    </FilterSortProvider>
  );
};
